// Core
import React, { useMemo } from 'react';

// Libraries
import { useTranslation } from 'react-i18next';

// Hooks
import { useBreakpoint } from '../../../../../../hooks/useBreakpoint';

// Plugins
import { runningStatusToIndicatorStatus } from '../../../../../../plugins/crusher';

// Components
import { Col } from '../../../../../../layout/elements/Col';
import { Paragraph, ParagraphSize, ParagraphWeight } from '../../../../../../layout/elements/Paragraph';
import { Row } from '../../../../../../layout/elements/Row';
import { StatusIndicator } from '../../../../../../layout/elements/StatusIndicator';

// View
import {
  IndicatorsContainer,
  StatusContainer,
  TitleContainer,
} from './Indicators.style';
import { IndicatorsProps } from './Indicators.types';

function Indicators({
  className,
  unitData,
  edgeStatus,
  testId,
}: IndicatorsProps): JSX.Element {
  // Dependencies
  const { t } = useTranslation();
  const { isXs } = useBreakpoint();

  const calculateIndicatorColSpan = useMemo((): {
    sm: number;
    xs: number;
  } => {
    let count: number = 2;
    if (unitData?.feedSuperiorEnable) count += 2;
    if (unitData?.dischargeSuperiorEnable) count += 1;

    if (count === 2) return { sm: 12, xs: 12 };
    return { sm: 8, xs: 8 };
  }, [unitData?.feedSuperiorEnable, unitData?.dischargeSuperiorEnable]);

  return (
    <IndicatorsContainer
      data-testid={testId}
      className={className}
    >
      <TitleContainer>
        <Paragraph
          weight={ParagraphWeight.bold}
          size={isXs ? ParagraphSize.sm : ParagraphSize.md}
        >
          {t('view.crusher.runningStatus.title')}
        </Paragraph>
      </TitleContainer>
      <StatusContainer>
        <Row style={{ rowGap: 12 }}>

          {/* Lubrication */}
          <Col
            xl={5}
            md={5}
            sm={calculateIndicatorColSpan.sm}
            xs={calculateIndicatorColSpan.xs}
          >
            <StatusIndicator
              text={t('view.crusher.runningStatus.lubrication')}
              status={runningStatusToIndicatorStatus(
                unitData?.lubePumpRunningStatus,
                edgeStatus ? edgeStatus.edgeStatus : null,
                unitData?.timestamp,
              )}
              testId="lubrication-running-status"
            />
          </Col>

          {/* Crusher Motor */}
          <Col
            xl={5}
            md={5}
            sm={calculateIndicatorColSpan.sm}
            xs={calculateIndicatorColSpan.xs}
          >
            <StatusIndicator
              text={t('view.crusher.runningStatus.motor')}
              status={runningStatusToIndicatorStatus(
                unitData?.crusherMotorStatus,
                edgeStatus ? edgeStatus.edgeStatus : null,
                unitData?.timestamp,
              )}
              testId="crusher-motor-running-status"
            />
          </Col>

          {/* Feeder */}
          {unitData?.feedSuperiorEnable && (
            <Col
              xl={5}
              md={5}
              sm={calculateIndicatorColSpan.sm}
              xs={calculateIndicatorColSpan.xs}
            >
              <StatusIndicator
                text={t('view.crusher.runningStatus.feeder')}
                status={runningStatusToIndicatorStatus(
                  unitData?.feederRunningStatus,
                  edgeStatus ? edgeStatus.edgeStatus : null,
                  unitData?.timestamp,
                )}
                testId="feeder-running-status"
              />
            </Col>
          )}

          {/* Bin Feeder */}
          {unitData?.feedSuperiorEnable && (
            <Col
              xl={5}
              md={5}
              sm={calculateIndicatorColSpan.sm}
              xs={calculateIndicatorColSpan.xs}
            >
              <StatusIndicator
                text={t('view.crusher.vsi.runningStatus.binFeeder')}
                status={runningStatusToIndicatorStatus(
                  unitData?.binFeederRunningStatus,
                  edgeStatus ? edgeStatus.edgeStatus : null,
                  unitData?.timestamp,
                )}
                testId="bin-feeder-running-status"
              />
            </Col>
          )}

          {/* Discharge */}
          {unitData?.dischargeSuperiorEnable && (
            <Col
              xl={4}
              md={4}
              sm={calculateIndicatorColSpan.sm}
              xs={calculateIndicatorColSpan.xs}
            >
              <StatusIndicator
                text={t('view.crusher.runningStatus.discharge')}
                status={runningStatusToIndicatorStatus(
                  unitData?.dischargeRunningStatus,
                  edgeStatus ? edgeStatus.edgeStatus : null,
                  unitData?.timestamp,
                )}
                testId="discharge-running-status"
              />
            </Col>
          )}
        </Row>
      </StatusContainer>
    </IndicatorsContainer>
  );
}

export { Indicators };
